import { Location, LocationType } from '../SearchLocation.types'

/**
 * Validation function type - returns true if valid, or error message string if invalid
 */
export type LocationValidator = (location: Location) => boolean | string

/**
 * Validate a location using a custom validator function
 * @param location - Location to validate
 * @param validator - Optional validation function
 * @returns Object with isValid boolean and error message
 */
export const validateLocation = (
  location: Location,
  validator?: LocationValidator
): { isValid: boolean; error: string | null } => {
  if (!validator) {
    return { isValid: true, error: null }
  }

  const result = validator(location)

  if (typeof result === 'boolean') {
    return { isValid: result, error: result ? null : 'Invalid location' }
  }

  // result is an error message string
  return { isValid: false, error: result }
}

/**
 * Check if a location type is in the allowed list
 * @param type - Location type to check
 * @param allowedTypes - Array of allowed types
 * @returns True if type is allowed
 */
export const isValidLocationType = (
  type: LocationType,
  allowedTypes: LocationType[]
): boolean => {
  return allowedTypes.includes(type)
}

/**
 * Check if location has required fields
 * @param location - Location to check
 * @returns True if location has all required fields
 */
export const hasRequiredFields = (location: Location): boolean => {
  return !!(location.id && location.location_search && location.location_type)
}
